import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import {
  Button,
  Alert,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { LayoutContainer } from "../../components/layoutContainer";
import ReCAPTCHA from "react-google-recaptcha";
import InputMask from "react-input-mask";
import { ModeloEnvio } from "./utils/phoneValidation";
import {
  useHomeController,
  useResgatarCodigoController,
} from "./useController";
import i18n from "../../translate/i18n";
import { Trans, useTranslation } from "react-i18next";
import { useCountryStore } from "../../stores/useCountry";
import { Float } from "../../components/selectRegion";

export default function Home() {
  // #region States
  const {
    alertMessage,
    setAlertMessage,
    idCliente,
    statusEnvio,
    handleTextInput,
    handleEnviarArpCodigo,
    handleChangeCaptcha,
    token,
    maskedPhone,
    isVisibleRecaptcha,
    setIsVisibleRecaptcha,
    modeloEnvioBackup,
    handleArpCodigoSms,
    handleArpCodigoWhatsapp,
    captcha,
  } = useHomeController();
  // #endregion

  const language = i18n.language as string;
  const { country } = useCountryStore();
  const { t } = useTranslation();

  const [loadingType, setLoadingType] = useState<"sms" | "whatsapp" | null>(
    null,
  );

  useEffect(() => {
    if (alertMessage) {
      const timer = setTimeout(() => {
        setAlertMessage(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [alertMessage]);
  // #endregion
  return (
    <LayoutContainer>
      <div className={styles.container}>
        <section className={styles.containerTexts}>
          <Typography variant="h3" className={styles.title}>
            {t("confirmPhone")}
          </Typography>
          <h6>
            <Trans
              i18nKey="confirmPhoneDescription"
              components={{ strong: <strong /> }}
            />
          </h6>
        </section>
        <section className={styles.content}>
          {idCliente ? (
            <ResgatarCodigo
              idCliente={idCliente}
              handleEnviarArpCodigo={handleEnviarArpCodigo}
              statusEnvio={statusEnvio}
              setAlertMessage={setAlertMessage}
              phone={maskedPhone}
              token={token}
              setIsVisibleRecaptcha={setIsVisibleRecaptcha}
              modeloEnvio={modeloEnvioBackup}
            />
          ) : (
            <DeezerPhoneConfirmation
              handleEnviarArpCodigoSms={handleArpCodigoSms}
              handleEnviarArpCodigoWhatsapp={handleArpCodigoWhatsapp}
              handleTextInput={handleTextInput}
              handleChangeCaptcha={handleChangeCaptcha}
              captcha={captcha}
              loadingType={loadingType}
              setLoadingType={setLoadingType}
            />
          )}
        </section>
        <section>
          {alertMessage && <Alert severity="warning">{alertMessage}</Alert>}
        </section>
        <section
          style={{
            justifyContent: "center",
            display: isVisibleRecaptcha ? "flex" : "none",
          }}
        >
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_KEY + ""}
            hl={
              language.length > 2
                ? language.slice(0, 2) + "-" + language.slice(2)
                : language
            }
            onChange={handleChangeCaptcha}
            ref={captcha}
          />
        </section>
      </div>
    </LayoutContainer>
  );
}

export const DeezerPhoneConfirmation = ({
  handleTextInput,
  handleEnviarArpCodigoWhatsapp,
  handleEnviarArpCodigoSms,
  loadingType,
  setLoadingType,
}: {
  handleTextInput: any;
  handleChangeCaptcha: any;
  captcha: any;
  handleEnviarArpCodigoWhatsapp: any;
  handleEnviarArpCodigoSms: any;
  loadingType: "sms" | "whatsapp" | null;
  setLoadingType: (type: "sms" | "whatsapp" | null) => void;
}) => {
  const { t } = useTranslation();
  const { country } = useCountryStore();

  const getFormatMask = () => {
    switch (country) {
      case "BR":
        return "(99) 99999-9999";
      case "CO":
        return "(999) 999-9999";
      case "PE":
        return "(99) 9999-999";
      case "EC":
        return "(99) 999-9999";
      default:
        return "(99) 99999-9999";
    }
  };

  const handleSms = async () => {
    setLoadingType("sms");
    try {
      await handleEnviarArpCodigoSms();
    } finally {
      setLoadingType(null);
    }
  };

  const handleWhatsapp = async () => {
    setLoadingType("whatsapp");
    try {
      await handleEnviarArpCodigoWhatsapp();
    } finally {
      setLoadingType(null);
    }
  };

  const isLoading = loadingType !== null;

  return (
    <>
      <div>
        <Float />
        <label htmlFor="input-telefone" className={styles.labelInput}>
          {t("confirmPhone")}
        </label>
        <InputMask
          id="input-telefone"
          mask={getFormatMask()}
          maskChar=" "
          onChange={handleTextInput}
          className={styles.textInput}
        />
      </div>
      <div>
        <Button
          type="submit"
          sx={{ textTransform: "none" }}
          onClick={handleSms}
          fullWidth
          disabled={isLoading}
          startIcon={
            loadingType === "sms" ? <CircularProgress size={18} /> : null
          }
        >
          {loadingType === "sms" ? "Enviando..." : t("confirmBySMS")}
        </Button>
      </div>
      <div>
        <Button
          type="submit"
          sx={{ textTransform: "none" }}
          onClick={handleWhatsapp}
          fullWidth
          disabled={isLoading}
          startIcon={
            loadingType === "whatsapp" ? <CircularProgress size={18} /> : null
          }
        >
          {loadingType === "whatsapp" ? "Enviando..." : t("confirmByWhatsApp")}
        </Button>
      </div>
    </>
  );
};

export const ResgatarCodigo = ({
  idCliente,
  handleEnviarArpCodigo,
  statusEnvio,
  setAlertMessage,
  phone,
  token,
  setIsVisibleRecaptcha,
  modeloEnvio,
}: {
  idCliente: string | null;
  handleEnviarArpCodigo: ({
    modeloEnvio,
    eReenvio,
  }: {
    modeloEnvio: ModeloEnvio;
    eReenvio: boolean;
  }) => Promise<void>;
  statusEnvio: any;
  setAlertMessage: (message: string) => void;
  phone?: string;
  token?: string;
  setIsVisibleRecaptcha: (value: boolean) => void;
  modeloEnvio: ModeloEnvio;
}) => {
  const {
    fullCode,
    handleChangeCodigo,
    handleRenviarArpCodigo,
    handleResgataCodigo,
    countFormatMinSec,
    isCounting,
  } = useResgatarCodigoController({
    idCliente,
    handleEnviarArpCodigo,
    statusEnvio,
    setAlertMessage,
    phone,
    token,
    setIsVisibleRecaptcha,
    modeloEnvio,
  });
  const { t } = useTranslation();

  return (
    <>
      <Box
        width={"100%"}
        display={"flex"}
        flexDirection={"column"}
        gap={"10px"}
      >
        <Typography padding={"10px 40px"} variant="body2" textAlign={"center"}>
          {t("enterCode")}{" "}
          {modeloEnvio === ModeloEnvio.SMS ? "SMS" : "WhatsApp"}
        </Typography>
        <Box
          width={"100%"}
          display={"flex"}
          gap={"10px"}
          justifyContent={"center"}
        >
          <InputMask
            id="input-codigo1"
            mask="99-99-99-99"
            maskChar=" "
            onChange={handleChangeCodigo}
            className={styles.textInput}
          />
        </Box>
        <Button
          variant="text"
          onClick={handleRenviarArpCodigo}
          disabled={isCounting || statusEnvio?.enviosRestantes <= 0}
          className="button-primary"
          fullWidth
          style={{
            color: "#a238ff",
            opacity: isCounting || statusEnvio?.enviosRestantes <= 0 ? 0.6 : 1,
          }}
        >
          {statusEnvio?.enviosRestantes <= 0
            ? t("resendLimitReached")
            : t("resendCodeWithTimer", { time: countFormatMinSec })}
        </Button>
      </Box>
      <Box width={"100%"}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          style={{
            borderRadius: "15px",
            padding: "10px 0px",
            textTransform: "none",
            fontSize: "20px",
            backgroundColor: fullCode ? "#a238ff" : "#4E4E4E",
            color: fullCode ? "#FFFFFF" : "#757575",
          }}
          disabled={!fullCode}
          onClick={handleResgataCodigo}
        >
          {t("redeemCodeMin")}
        </Button>
      </Box>
    </>
  );
};
